import { useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  FileText,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  useCreateProposalTemplate,
  useUpdateProposalTemplate,
  type ProposalTemplate,
  type SectionType,
  type TemplateSectionConfig,
  type TemplateType,
} from "@/hooks/use-proposals";

// Mejoras L/I (changelog cliente): editor de templates de propuesta.
// Diálogo unificado para crear o editar un template completo: cabecera
// (nombre, descripción, tipo, default) + lista ordenable de secciones.

const SECTION_TYPE_OPTIONS: { value: SectionType; label: string }[] = [
  { value: "PORTADA", label: "Portada" },
  { value: "INDICE", label: "Índice" },
  { value: "INTRODUCCION", label: "Introducción" },
  { value: "METODOLOGIA", label: "Metodología" },
  { value: "EQUIPO_TRABAJO", label: "Equipo de trabajo" },
  { value: "EXPERIENCIA", label: "Experiencia" },
  { value: "PLANIFICACION", label: "Planificación" },
  { value: "MEDIOS_TECNICOS", label: "Medios técnicos" },
  { value: "MEJORAS", label: "Mejoras" },
  { value: "OFERTA_ECONOMICA", label: "Oferta económica" },
  { value: "ANEXOS", label: "Anexos" },
  { value: "CUMPLIMIENTO", label: "Cumplimiento" },
  { value: "CUSTOM", label: "Personalizada" },
];

const TEMPLATE_TYPE_OPTIONS: { value: TemplateType; label: string }[] = [
  { value: "TECNICA", label: "Técnica" },
  { value: "ECONOMICA", label: "Económica" },
  { value: "COMPLETA", label: "Completa" },
];

interface TemplateEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Si se pasa, el diálogo abre en modo edición. */
  template?: ProposalTemplate | null;
}

interface DraftSection extends TemplateSectionConfig {
  // Identificador local para react keys (no se envía al backend).
  localId: string;
}

function emptySection(order: number): DraftSection {
  return {
    localId: crypto.randomUUID(),
    title: "",
    section_type: "CUSTOM",
    section_order: order,
    context_from_previous: false,
  };
}

export function TemplateEditorDialog({
  isOpen,
  onClose,
  template,
}: TemplateEditorDialogProps) {
  const isEdit = !!template;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [templateType, setTemplateType] = useState<TemplateType>("TECNICA");
  const [isDefault, setIsDefault] = useState(false);
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useCreateProposalTemplate();
  const updateMutation = useUpdateProposalTemplate();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Cuando abre, sincronizamos el estado con el template recibido (o reset
  // para "nuevo"). Necesario porque el padre reusa el mismo diálogo entre
  // aperturas.
  useEffect(() => {
    if (!isOpen) return;
    if (template) {
      setName(template.name);
      setDescription(template.description ?? "");
      setTemplateType(template.template_type);
      setIsDefault(template.is_default);
      setSections(
        template.sections_config.map((s, idx) => ({
          ...s,
          localId: crypto.randomUUID(),
          section_order: idx,
        }))
      );
    } else {
      setName("");
      setDescription("");
      setTemplateType("TECNICA");
      setIsDefault(false);
      setSections([emptySection(0)]);
    }
    setFormError(null);
    createMutation.reset();
    updateMutation.reset();
    // eslint-disable-next-line
  }, [isOpen, template?.id]);

  const updateSection = (localId: string, patch: Partial<DraftSection>) => {
    setSections((prev) =>
      prev.map((s) => (s.localId === localId ? { ...s, ...patch } : s))
    );
  };

  const addSection = () => {
    setSections((prev) => [...prev, emptySection(prev.length)]);
  };

  const removeSection = (localId: string) => {
    setSections((prev) =>
      prev
        .filter((s) => s.localId !== localId)
        .map((s, idx) => ({ ...s, section_order: idx }))
    );
  };

  const moveSection = (localId: string, direction: -1 | 1) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.localId === localId);
      if (idx === -1) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((s, i) => ({ ...s, section_order: i }));
    });
  };

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError("El nombre de la plantilla es obligatorio.");
      return;
    }
    if (sections.length === 0) {
      setFormError("La plantilla debe tener al menos una sección.");
      return;
    }
    const invalidSection = sections.find((s) => !s.title.trim());
    if (invalidSection) {
      setFormError("Todas las secciones deben tener un título.");
      return;
    }
    setFormError(null);

    // Strip localId antes de enviar al backend.
    const sectionsConfig: TemplateSectionConfig[] = sections.map((s, idx) => ({
      title: s.title.trim(),
      section_type: s.section_type,
      section_order: idx,
      ai_prompt: s.ai_prompt?.trim() || undefined,
      suggested_word_count: s.suggested_word_count || undefined,
      context_from_previous: s.context_from_previous,
    }));

    const onSuccess = () => onClose();

    if (isEdit && template) {
      updateMutation.mutate(
        {
          templateId: template.id,
          name: trimmedName,
          description: description.trim() || undefined,
          sections_config: sectionsConfig,
          is_default: isDefault,
        },
        { onSuccess }
      );
    } else {
      createMutation.mutate(
        {
          name: trimmedName,
          description: description.trim() || undefined,
          template_type: templateType,
          sections_config: sectionsConfig,
          is_default: isDefault,
        },
        { onSuccess }
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Editar plantilla" : "Nueva plantilla"}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "Editar plantilla" : "Nueva plantilla de propuesta"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Cabecera */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Propuesta CIMI"
                disabled={isSaving}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value as TemplateType)}
                disabled={isSaving || isEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                title={
                  isEdit
                    ? "El tipo no se puede cambiar en una plantilla existente"
                    : undefined
                }
              >
                {TEMPLATE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción{" "}
              <span className="text-gray-400 font-normal">opcional</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Para qué sirve esta plantilla, qué licitaciones cubre..."
              rows={2}
              disabled={isSaving}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:opacity-50"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={isSaving}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Usar como plantilla por defecto</span>
          </label>

          {/* Secciones */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">
                Secciones de la plantilla
              </h3>
              <button
                type="button"
                onClick={addSection}
                disabled={isSaving}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5" />
                Añadir sección
              </button>
            </div>

            {sections.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                Añade al menos una sección.
              </p>
            )}

            <ul className="space-y-3">
              {sections.map((s, idx) => (
                <li
                  key={s.localId}
                  className="border border-gray-200 rounded-md p-3 bg-gray-50"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-1 pt-1">
                      <button
                        type="button"
                        onClick={() => moveSection(s.localId, -1)}
                        disabled={idx === 0 || isSaving}
                        className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Subir sección"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSection(s.localId, 1)}
                        disabled={idx === sections.length - 1 || isSaving}
                        className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Bajar sección"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={s.title}
                        onChange={(e) =>
                          updateSection(s.localId, { title: e.target.value })
                        }
                        placeholder="Título de la sección"
                        disabled={isSaving}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      />
                      <select
                        value={s.section_type}
                        onChange={(e) =>
                          updateSection(s.localId, {
                            section_type: e.target.value as SectionType,
                          })
                        }
                        disabled={isSaving}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      >
                        {SECTION_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={s.suggested_word_count ?? ""}
                        onChange={(e) =>
                          updateSection(s.localId, {
                            suggested_word_count: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          })
                        }
                        placeholder="Palabras objetivo (opcional)"
                        min={50}
                        max={10000}
                        step={50}
                        disabled={isSaving}
                        className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      />
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!s.context_from_previous}
                          onChange={(e) =>
                            updateSection(s.localId, {
                              context_from_previous: e.target.checked,
                            })
                          }
                          disabled={isSaving}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Usar contexto de secciones anteriores
                      </label>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeSection(s.localId)}
                      disabled={isSaving}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      aria-label="Eliminar sección"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {formError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {formError}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : isEdit ? (
              "Guardar cambios"
            ) : (
              "Crear plantilla"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TemplateEditorDialog;
